// src/screens/CreateMatch.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { CalendarDays, Home, Bus, Smartphone, Clock, Users, CheckSquare, Square } from "lucide-react";
import i18n from "../i18n";

type Player = {
  firstName: string;
  lastName: string;
  number: number;
  position: string;
  photoUrl?: string;
};

type Team = {
  id: string;
  team_name: string;
  players: Player[] | null;
};

/* ===== i18n mini-hook (pattern AllMatches / AllPlays) ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

export const CreateMatch = () => {
  const { t } = useI18nSafe();

  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  // joueurs de l'équipe sélectionnée (source)
  const [teamPlayers, setTeamPlayers] = useState<Player[]>([]);
  // joueurs convoqués (sélection utilisateur) — stocke les numéros
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);

  const [opponent, setOpponent] = useState("");
  const [date, setDate] = useState("");          // "YYYY-MM-DD"
  const [time, setTime] = useState("");          // "HH:MM"
  const [location, setLocation] = useState<"domicile" | "extérieur">("domicile");
  const [isLive, setIsLive] = useState(false);
  const [search, setSearch] = useState("");      // filtre rapide par nom/num

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTeams = async () => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        navigate("/login");
        return;
      }

      const { data, error } = await supabase
        .from("teams")
        .select("id, team_name, players")
        .eq("user_id", user.id)
        .or("archived.is.null,archived.eq.false");

      if (error) {
        console.error("Erreur chargement équipes :", error);
      } else {
        const list = data || [];
        setTeams(list);
        if (list.length > 0) {
          const first = list[0];
          setSelectedTeamId(first.id);
          const p = (first.players ?? []) as Player[];
          setTeamPlayers(p);
          // ✅ Par défaut on pré-sélectionne tout le monde
          setSelectedNumbers(p.map(pl => pl.number));
        }
      }
    };

    fetchTeams();
  }, [navigate]);

  // Quand l'équipe change, on recharge joueurs + sélection par défaut
  useEffect(() => {
    if (!selectedTeamId) return;
    const team = teams.find((t) => t.id === selectedTeamId);
    const p = (team?.players ?? []) as Player[];
    setTeamPlayers(p);
    // ✅ Comportement: tout sélectionner par défaut (tu peux mettre [] si tu préfères)
    setSelectedNumbers(p.map(pl => pl.number));
  }, [selectedTeamId, teams]);

  // Liste filtrée par recherche
  const filteredPlayers = useMemo(() => {
    if (!search.trim()) return teamPlayers;
    const q = search.toLowerCase();
    return teamPlayers.filter(p =>
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
      `${p.lastName} ${p.firstName}`.toLowerCase().includes(q) ||
      String(p.number).includes(q) ||
      (p.position ?? "").toLowerCase().includes(q)
    );
  }, [teamPlayers, search]);

  const togglePlayer = (num: number) => {
    setSelectedNumbers(prev =>
      prev.includes(num) ? prev.filter(n => n !== num) : [...prev, num]
    );
  };

  const selectAll = () => setSelectedNumbers(teamPlayers.map(p => p.number));
  const deselectAll = () => setSelectedNumbers([]);

  const handleSubmit = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      alert(t("creatematch:alerts.login_required"));
      return;
    }

    if (!opponent.trim() || !date || !time || !location || !selectedTeamId) {
      alert(t("creatematch:alerts.fill_all_fields"));
      return;
    }

    // Construit l'array final de joueurs convoqués (snapshot)
    const invitedPlayers: Player[] = teamPlayers
      .filter(p => selectedNumbers.includes(p.number))
      .map(p => ({ ...p }));

    // Combine date + time -> "YYYY-MM-DDTHH:MM:SS"
    const dateTime = `${date}T${time.length === 5 ? `${time}:00` : time}`;

    const { data, error } = await supabase
      .from("matches")
      .insert([
        {
          user_id: user.id,
          opponent: opponent.trim(),
          date: dateTime,
          location,
          live: isLive,
          players: invitedPlayers,     // ✅ uniquement les convoquées
          team_id: selectedTeamId,
        },
      ])
      .select("id")
      .single();

    if (error || !data) {
      console.error("Erreur création match :", error);
      alert(t("creatematch:alerts.create_error"));
    } else {
      alert(t("creatematch:alerts.create_success"));
      if (isLive) navigate(`/livestats/${data.id}`);
      else navigate("/allmatchs");
    }
  };

  return (
    <section className="min-h-screen w-full p-6 mx-auto bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <h1 className="text-2xl font-bold text-center mb-6 text-orange-600 dark:text-orange-400">
        {t("creatematch:title")}
      </h1>

      <div className="max-w-5xl mx-auto rounded-2xl border bg-white border-gray-200 shadow-sm p-6 dark:bg-gray-900 dark:border-gray-800">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Colonne gauche: Infos match */}
          <div className="space-y-4">
            {/* Équipe */}
            {teams.length > 1 ? (
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
                  {t("creatematch:fields.team")}
                </label>
                <select
                  value={selectedTeamId ?? ""}
                  onChange={(e) => setSelectedTeamId(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 border
                             bg-white border-gray-300 text-gray-900
                             focus:outline-none focus:ring-2 focus:ring-orange-400/60
                             dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                >
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.team_name}
                    </option>
                  ))}
                </select>
              </div>
            ) : teams.length === 1 ? (
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t("creatematch:fields.team")}</div>
                <div className="inline-flex items-center px-2 py-1 rounded border
                                bg-blue-50 text-blue-700 border-blue-200
                                dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/40">
                  {teams[0]?.team_name || t("creatematch:defaults.my_team")}
                </div>
              </div>
            ) : null}

            {/* Adversaire */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
                {t("creatematch:fields.opponent")}
              </label>
              <input
                type="text"
                value={opponent}
                onChange={(e) => setOpponent(e.target.value)}
                placeholder={t("creatematch:placeholders.opponent")}
                className="w-full rounded-lg px-3 py-2 border
                           bg-white border-gray-300 text-gray-900 placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
              />
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
                {t("creatematch:fields.date")}
              </label>
              <div className="relative">
                <CalendarDays className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full appearance-none text-sm rounded-lg pl-9 pr-3 py-2 border
                 bg-white border-gray-300 text-gray-900
                 focus:outline-none focus:ring-2 focus:ring-orange-400/60
                 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                />
              </div>
            </div>

            {/* Heure */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
                {t("creatematch:fields.time")}
              </label>
              <div className="relative">
                <Clock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  step={60}
                  className="w-full appearance-none text-sm rounded-lg pl-9 pr-3 py-2 border
                 bg-white border-gray-300 text-gray-900
                 focus:outline-none focus:ring-2 focus:ring-orange-400/60
                 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                />
              </div>
            </div>

            {/* Lieu */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
                {t("creatematch:fields.location")}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setLocation("domicile")}
                  className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border
                              ${location === "domicile"
                      ? "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-900/30"
                      : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"}`}
                >
                  <Home className="w-4 h-4" />
                  {t("creatematch:location.home")}
                </button>
                <button
                  type="button"
                  onClick={() => setLocation("extérieur")}
                  className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border
                              ${location === "extérieur"
                      ? "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/20 dark:text-orange-300 dark:border-orange-900/30"
                      : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 dark:hover:bg-gray-700"}`}
                >
                  <Bus className="w-4 h-4" />
                  {t("creatematch:location.away")}
                </button>
              </div>
            </div>

            {/* Live */}
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isLive}
                onChange={(e) => setIsLive(e.target.checked)}
                className="h-4 w-4 accent-orange-500"
              />
              <span className="inline-flex items-center gap-1 text-gray-800 dark:text-gray-200">
                <Smartphone className="w-4 h-4" />
                {t("creatematch:fields.live")}
              </span>
            </label>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              className="mt-4 bg-[#fc7825] hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-lg transition
                         focus:outline-none focus:ring-2 focus:ring-orange-400/60 w-full sm:w-auto"
            >
              {t("creatematch:actions.create")}
            </button>
          </div>

          {/* Colonne droite: Sélection des joueuses */}
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="inline-flex items-center gap-2">
                <Users className="w-5 h-5 text-gray-700 dark:text-gray-200" />
                <span className="font-semibold">
                  {t("creatematch:fields.players", { defaultValue: "Joueuses convoquées" })}
                </span>
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {selectedNumbers.length}/{teamPlayers.length}
              </span>
            </div>

            {/* Actions sélection */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={selectAll}
                className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50
                           dark:border-gray-700 dark:hover:bg-gray-800"
                title={t("creatematch:actions.select_all", { defaultValue: "Tout sélectionner" })}
              >
                <CheckSquare className="w-4 h-4" />
                {t("creatematch:actions.select_all", { defaultValue: "Tout sélectionner" })}
              </button>
              <button
                type="button"
                onClick={deselectAll}
                className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50
                           dark:border-gray-700 dark:hover:bg-gray-800"
                title={t("creatematch:actions.clear_all", { defaultValue: "Tout désélectionner" })}
              >
                <Square className="w-4 h-4" />
                {t("creatematch:actions.clear_all", { defaultValue: "Tout désélectionner" })}
              </button>

              {/* Recherche */}
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("creatematch:placeholders.search_player", { defaultValue: "Rechercher (nom/numéro/poste)" })}
                className="ml-auto w-full sm:w-64 rounded-lg px-3 py-2 border
                           bg-white border-gray-300 text-gray-900 placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
              />
            </div>

            {/* Liste des joueuses */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[420px] overflow-auto pr-1">
              {filteredPlayers.length === 0 && (
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t("creatematch:empty.no_players", { defaultValue: "Aucune joueuse trouvée." })}
                </div>
              )}

              {filteredPlayers.map((p) => {
                const checked = selectedNumbers.includes(p.number);
                return (
                  <label
                    key={p.number}
                    className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition
                                ${checked
                        ? "bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-900/30"
                        : "bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700"}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePlayer(p.number)}
                      className="h-4 w-4 accent-orange-500"
                    />
                    {p.photoUrl ? (
                      <img
                        src={p.photoUrl}
                        alt={`${p.firstName} ${p.lastName}`}
                        className="w-9 h-9 rounded-full object-cover border border-gray-200 dark:border-gray-700"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-semibold">
                        {p.firstName?.[0]}{p.lastName?.[0]}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        #{p.number} — {p.firstName} {p.lastName}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                        {p.position || t("creatematch:defaults.position", { defaultValue: "Poste" })}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
